import { computed, type ReadonlySignal, signal } from "@preact/signals-core";

export const HISTORY_LIMIT = 50;

// `merge`: consecutive entries with the same key become one, labelled as the
// first, such as the renames typed while one name field keeps focus, when
// `combine` joins them (null: they do not, and the later starts its own).
// `mutationIds`: the writes of a step that took several (a group's), the first
// of which is `mutationId`.
export type Entry<C = unknown> = { mutationId: string; mutationIds?: string[]; label: string; change: C; merge?: string };

// Every write the entry's step made.
export const idsOf = (entry: Entry<unknown>) => entry.mutationIds ?? [entry.mutationId];

export type History<C = unknown> = ReturnType<typeof createHistory<C>>;

// One per scene for as long as the tab lives, so a visit to another scene and
// back keeps it; a reload starts empty.
const histories = new Map<string, History<unknown>>();

export function historyFor<C = unknown>(sceneId: string): History<C> {
	if (!histories.has(sceneId)) histories.set(sceneId, createHistory());
	return histories.get(sceneId) as History<C>;
}

function createHistory<C>() {
	let shape: string | null = null;
	const undone = signal<Entry<C>[]>([]);
	const redone = signal<Entry<C>[]>([]);
	const top = (stack: ReadonlySignal<Entry<C>[]>) => computed(() => stack.value.at(-1) ?? null);
	const [nextUndo, nextRedo] = [top(undone), top(redone)];

	function pushUndo(entry: Entry<C>) {
		undone.value = [...undone.peek(), entry].slice(-HISTORY_LIMIT);
	}

	function record(entry: Entry<C>, combine?: (earlier: C, later: C) => C | null) {
		const stack = undone.peek();
		const last = stack.at(-1);
		const merged = entry.merge && last?.merge === entry.merge ? (combine ? combine(last.change, entry.change) : entry.change) : null;
		if (merged) undone.value = [...stack.slice(0, -1), { ...entry, label: last.label, change: merged }];
		else pushUndo(entry);
		redone.value = [];
	}

	function take(stack: typeof undone) {
		const entries = stack.peek();
		if (!entries.length) return undefined;
		stack.value = entries.slice(0, -1);
		return entries.at(-1);
	}

	// The entries these writes made (a group's whole, whichever write it was),
	// and every entry whose change `touched` names, from both stacks.
	function forget(mutationIds: string[], touched: (change: C) => boolean = () => false) {
		const ids = new Set(mutationIds);
		for (const stack of [undone, redone]) {
			const kept = stack.peek().filter((entry) => !idsOf(entry).some((id) => ids.has(id)) && !touched(entry.change));
			if (kept.length !== stack.peek().length) stack.value = kept;
		}
	}

	// The scene's shape as last seen: a different one drops both stacks, the first sighting only notes it.
	function keep(next: string | null) {
		if (next === null) return;
		if (shape !== null && shape !== next) clear();
		shape = next;
	}

	function clear() {
		undone.value = [];
		redone.value = [];
	}

	return {
		canUndo: computed(() => undone.value.length > 0),
		canRedo: computed(() => redone.value.length > 0),
		nextUndo,
		nextRedo,
		undoLabel: computed(() => nextUndo.value?.label ?? null),
		redoLabel: computed(() => nextRedo.value?.label ?? null),
		record,
		forget,
		takeUndo: () => take(undone),
		takeRedo: () => take(redone),
		pushUndo,
		pushRedo(entry: Entry<C>) {
			redone.value = [...redone.peek(), entry];
		},
		keep,
		clear,
	};
}

type KeyInit = Pick<KeyboardEvent, "key" | "code" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">;

// The letter printed on the key wins; a key that is not a Latin letter (a
// Cyrillic or Greek layout) falls back to where it sits. Cmd+Y is the Mac
// browsers' History, so only Ctrl+Y redoes.
export function historyKey(event: KeyInit): "undo" | "redo" | null {
	if (event.altKey || !(event.ctrlKey || event.metaKey)) return null;
	const letter = /^[a-z]$/i.test(event.key) ? event.key.toLowerCase() : ({ KeyZ: "z", KeyY: "y" }[event.code] ?? null);
	if (letter === "z") return event.shiftKey ? "redo" : "undo";
	if (letter === "y" && event.ctrlKey && !event.metaKey && !event.shiftKey) return "redo";
	return null;
}

// Ctrl/Cmd+C and Ctrl/Cmd+V, matched like `historyKey`; a Shift or Alt press is the browser's own (paste as plain text).
export function clipboardKey(event: KeyInit): "copy" | "paste" | null {
	if (event.altKey || event.shiftKey || !(event.ctrlKey || event.metaKey)) return null;
	const letter = /^[a-z]$/i.test(event.key) ? event.key.toLowerCase() : ({ KeyC: "c", KeyV: "v" }[event.code] ?? null);
	return letter === "c" ? "copy" : letter === "v" ? "paste" : null;
}

// Ctrl/Cmd+A, matched like `clipboardKey`.
export function selectAllKey(event: KeyInit): boolean {
	if (event.altKey || event.shiftKey || !(event.ctrlKey || event.metaKey)) return false;
	return /^[a-z]$/i.test(event.key) ? event.key.toLowerCase() === "a" : event.code === "KeyA";
}

export function historyShortcuts(mac: boolean) {
	return mac ? { undo: "⌘Z", redo: "⌘⇧Z" } : { undo: "Ctrl+Z", redo: "Ctrl+Shift+Z" };
}
